import { useCallback, useEffect, useState } from "react";
import { Button, Card, EmptyState, NoticeBox, Switch, Tags } from "../../ui";
import { fetchCopilotPreferencesAPI, updateCopilotPreferencesAPI } from "../../../utils/api/copilot";
import { navigateInApp } from "../../copilot/copilotLinks";
import { settingsError } from "./copilotSettingsText";
import { useConfirmedAction } from "../useConfirmedAction";

// ---- General ---------------------------------------------------------------------------------
export function GeneralSettings({ t, lang, settings, canManageAuto, onOpenTab }) {
  const g = t.general;
  const auto = settings.autoReply;
  return (
    <div className="cs-stack">
      <Card title={g.supportTitle} subtitle={g.supportBody} hoverable={false}>
        <div className="cs-status-row">
          <span className="cs-status-label">{g.autoStatus}</span>
          <Tags variant={auto.enabled ? "success" : "neutral"}>{auto.enabled ? t.on : t.off}</Tags>
        </div>
        <p className="cs-hint">{auto.enabled ? g.autoOn : g.autoOff}</p>
        <Button size="sm" variant="neutral" appearance="outline" onClick={() => onOpenTab("auto")}>{canManageAuto ? g.manageAuto : t.tabs.auto}</Button>
      </Card>

      <Card title={g.contentTitle} subtitle={g.contentBody} hoverable={false}>
        <dl className="cs-facts">
          {g.content.map(([tab, term, text]) => (
            <div key={tab}>
              <dt>{term}</dt>
              <dd>
                {text}{" "}
                <Button variant="primary" appearance="text" size="xs" onClick={() => onOpenTab(tab)}>{t.tabs[tab]}</Button>
              </dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card title={g.categoriesTitle} subtitle={g.categoriesBody} hoverable={false}>
        <ul className="cs-categories">
          {settings.categories.map((category) => (
            <li key={category.value}>
              <strong>{lang === "ar" ? category.labelAr : category.labelEn}</strong>
              <span className="cs-muted">{lang === "ar" ? category.labelEn : category.labelAr}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title={g.behaviorTitle} hoverable={false}>
        <dl className="cs-facts">
          {g.behavior.map(([term, text]) => (
            <div key={term}>
              <dt>{term}</dt>
              <dd>{text}</dd>
            </div>
          ))}
        </dl>
        <p className="cs-hint">{g.fixedNote}</p>
      </Card>
    </div>
  );
}

// ---- Notifications (the existing per-user preferences; no second system) -------------------------
export function NotificationSettings({ t, lang }) {
  const confirmation = useConfirmedAction(lang);
  const n = t.notifications;
  const [state, setState] = useState({ status: "loading", email: true, inApp: true, error: null });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, status: "loading", error: null }));
    try {
      const data = await fetchCopilotPreferencesAPI();
      setState({ status: "ready", error: null, ...data.preferences });
    } catch (error) {
      setState((prev) => ({ ...prev, status: "error", error: settingsError(error, t) }));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Keep the current preference until the user confirms the requested change.
  const change = (key, value) => confirmation.ask(async () => {
    const previous = state[key];
    setState((prev) => ({ ...prev, [key]: value, error: null, saved: false }));
    try {
      const data = await updateCopilotPreferencesAPI({ [key]: value });
      setState((prev) => ({ ...prev, ...data.preferences, saved: true }));
    } catch (error) {
      setState((prev) => ({ ...prev, [key]: previous, error: settingsError(error, t), saved: false }));
      return false;
    }
  }, "toggle", n[key], !value, `${value ? t.on : t.off}. ${key === "email" ? n.emailHint : n.inAppHint}`);

  return (
    <div className="cs-stack">
      <Card title={n.title} subtitle={n.description} hoverable={false}>
        {state.status === "error" && (
          <EmptyState variant="error" action={<Button size="sm" variant="neutral" appearance="outline" onClick={load}>{t.retry}</Button>}>
            {n.loadError} {state.error}
          </EmptyState>
        )}
        {state.status !== "error" && (
          <>
            <div className="cs-pref">
              <Switch checked={Boolean(state.email)} disabled={state.status === "loading"} onChange={(e) => change("email", e.target.checked)} label={n.email} />
              <p className="cs-hint">{n.emailHint}</p>
            </div>
            <div className="cs-pref">
              <Switch checked={Boolean(state.inApp)} disabled={state.status === "loading"} onChange={(e) => change("inApp", e.target.checked)} label={n.inApp} />
              <p className="cs-hint">{n.inAppHint}</p>
            </div>
            {state.error && <p className="cs-error" role="alert">{state.error}</p>}
            {state.saved && <p className="cs-ok" role="status">{t.saved}</p>}
            <NoticeBox variant="info" size="sm">{n.required}</NoticeBox>
            <div className="cs-actions">
              <Button size="sm" variant="neutral" appearance="outline" onClick={() => navigateInApp("/auth/notifications")}>{n.openPage}</Button>
            </div>
          </>
        )}
      </Card>
      {confirmation.dialog}
    </div>
  );
}
